/**
 * Los papeles del inmueble, como lista.
 *
 * LO QUE HABÍA ANTES ERA UNA MENTIRA ÚTIL: una tarjeta llamada «Documentación obligatoria» con
 * dos filas fijas —«Escrituras» y «Certificado energético»— cuyo estado se calculaba buscando
 * un documento cuyo NOMBRE fuese exactamente esa palabra. Como nadie llama a un fichero
 * «Escrituras» a secas, ponía «Faltan 2 de 2» para siempre, aunque tuvieras la escritura
 * subida y visible dos centímetros más a la derecha.
 *
 * Esto es la lista de verdad: los once tipos que se piden en un alquiler, cada uno con su
 * estado, y el fichero se sube EN SU FILA. Sin salir de la pantalla y sin elegir la categoría
 * en un desplegable: la fila ya dice cuál es.
 *
 * SE DESCARGA POR LA API, no con la URL firmada que trae la ficha. La URL firmada es un bearer
 * token que vale para cualquiera que la tenga; el proxy además deja el acceso auditado, que es
 * lo que pregunta una inspección del RGPD.
 *
 * CADA FILA ES UNA ZONA DE DESTINO. Se arrastra el PDF desde la carpeta del ordenador hasta su
 * fila y se sube ahí. Es como se trabaja de verdad —la carpeta abierta al lado del navegador—
 * y ahorra el viaje de ida y vuelta por el diálogo de ficheros, doce veces seguidas.
 *
 * EL BOTÓN SE QUEDA. Arrastrar no existe en una tableta ni con un teclado, así que quitarlo
 * dejaría la pantalla sin ninguna forma accesible de subir un papel. Las dos vías hacen lo
 * mismo y acaban en la misma llamada.
 */
import { Fragment, useEffect, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEED_GROUPS, DEED_KINDS, DEED_KIND_LABELS, type DeedKind } from '@naar/shared';
import { ApiError, api, queryKeys, type PropertyDeedDto } from '../../api/client';
import { Card, CardHeader } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { useDocumentViewer } from '../../components/overlay/DocumentViewer';
import { formatShortDate } from '../../lib/format';
import styles from './PropertyDeeds.module.css';

/**
 * Los tipos que YA NO SE OFRECEN al subir: están en `DEED_KINDS` porque hay que saber
 * enseñarlos si alguien los guardó en su día, pero no aparecen como hueco que rellenar.
 */
const EN_GRUPOS = new Set<DeedKind>(DEED_GROUPS.flatMap((group) => [...group.kinds]));
const HEREDADOS = DEED_KINDS.filter((kind) => !EN_GRUPOS.has(kind));

/**
 * Tipos que admiten VARIOS documentos. El resto son un papel cada uno (subir otra nota simple
 * sustituye a la anterior); «Varios» es un cajón, y en un cajón caben muchos.
 */
const MULTIPLES = new Set<DeedKind>(['otro']);

/** Lo que acepta el servidor. Se comprueba aquí para decirlo antes de subir 8 MB para nada. */
const TIPOS_ACEPTADOS = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const EXTENSIONES_ACEPTADAS = /\.(pdf|jpe?g|png|webp)$/i;

/**
 * ¿Lo que se arrastra son FICHEROS?
 *
 * Arrastrar texto seleccionado o un enlace también dispara estos eventos, y sin esta
 * comprobación la lista entera se encendía como zona de destino al arrastrar una palabra por
 * la pantalla. `types` está disponible durante el arrastre; `files` todavía no.
 */
function traeFicheros(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}

function esAceptado(file: File): boolean {
  return TIPOS_ACEPTADOS.includes(file.type) || EXTENSIONES_ACEPTADAS.test(file.name);
}

export function PropertyDeeds({
  propertyId,
  canManage,
}: {
  propertyId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const visor = useDocumentViewer();
  const [subiendo, setSubiendo] = useState<DeedKind | null>(null);
  /** La fila que tiene el fichero encima ahora mismo. */
  const [sobre, setSobre] = useState<DeedKind | null>(null);
  /**
   * Hay un fichero sobrevolando la tarjeta.
   *
   * Sirve para encender TODAS las filas pendientes a la vez: si no, nadie descubre que se puede
   * soltar, porque una zona de destino que solo se ve cuando ya estás encima de ella llega
   * tarde. Se enciende al entrar en la tarjeta y se apaga al salir o al soltar.
   */
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const destinoRef = useRef<DeedKind | null>(null);

  const deeds = useQuery({
    queryKey: queryKeys.propertyDeeds(propertyId),
    queryFn: () => api.propertyDeeds(propertyId),
  });

  /*
   * RED DE SEGURIDAD DEL ARRASTRE.
   *
   * Mientras hay un fichero encima, las filas dejan de recibir el puntero en sus hijos para que
   * el resaltado no parpadee. Si el arrastre termina sin pasar por la tarjeta —se cancela con
   * `Esc`, se suelta en otra ventana, se sale por el borde del navegador— no llega ningún
   * `dragleave` y el estado se queda encendido: la lista se ve normal pero los botones no
   * responden, y no hay forma de saber por qué. `dragend` y `drop` en la ventana siempre
   * llegan, así que apagarlo desde ahí es lo único que lo garantiza.
   */
  useEffect(() => {
    function apagar() {
      setArrastrando(false);
      setSobre(null);
    }
    window.addEventListener('dragend', apagar);
    window.addEventListener('drop', apagar);
    return () => {
      window.removeEventListener('dragend', apagar);
      window.removeEventListener('drop', apagar);
    };
  }, []);

  const porTipo = new Map<DeedKind, PropertyDeedDto>();
  /** Todos los documentos de los tipos múltiples, en el orden en que se subieron. */
  const todosPorTipo = new Map<DeedKind, PropertyDeedDto[]>();
  for (const deed of deeds.data?.data ?? []) {
    // El más reciente de cada tipo manda: subir otra nota simple sustituye a la de hace un año.
    porTipo.set(deed.kind as DeedKind, deed);
    const kind = deed.kind as DeedKind;
    if (MULTIPLES.has(kind)) todosPorTipo.set(kind, [...(todosPorTipo.get(kind) ?? []), deed]);
  }

  const subir = useMutation({
    mutationFn: ({ kind, file }: { kind: DeedKind; file: File }) =>
      api.uploadDeed(propertyId, file, { kind }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.propertyDeeds(propertyId) });
      // El archivo general de la ficha también lo enseña: si no, aparece a medias.
      void queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
      toast.success(`${DEED_KIND_LABELS[variables.kind]} guardada.`);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido subir el documento.'),
    onSettled: () => setSubiendo(null),
  });

  /** Varios ficheros de golpe en un tipo múltiple: uno detrás de otro, y un solo aviso. */
  const subirVarios = useMutation({
    mutationFn: async ({ kind, files }: { kind: DeedKind; files: File[] }) => {
      let hechos = 0;
      for (const file of files) {
        await api.uploadDeed(propertyId, file, { kind });
        hechos += 1;
      }
      return hechos;
    },
    onSuccess: (hechos) => {
      toast.success(hechos === 1 ? 'Documento añadido.' : `${hechos} documentos añadidos.`);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se han podido subir todos los documentos.'),
    onSettled: () => {
      // También tras un fallo a medias: los que sí subieron tienen que aparecer.
      void queryClient.invalidateQueries({ queryKey: queryKeys.propertyDeeds(propertyId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
      setSubiendo(null);
    },
  });

  const ocupado = subir.isPending || subirVarios.isPending;

  const borrar = useMutation({
    mutationFn: (deedId: string) => api.deleteDeed(deedId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.propertyDeeds(propertyId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
      toast.success('Documento quitado de la lista. El fichero se conserva.');
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido quitar.'),
  });

  function empezarSubida(kind: DeedKind, file: File) {
    if (!esAceptado(file)) {
      toast.error(`«${file.name}» no vale: solo PDF, JPG, PNG o WEBP.`);
      return;
    }
    setSubiendo(kind);
    subir.mutate({ kind, file });
  }

  /** En un tipo múltiple se suben todos los ficheros válidos; el resto se avisa y se salta. */
  function empezarSubidaVarios(kind: DeedKind, files: File[]) {
    const validos = files.filter(esAceptado);
    const malos = files.length - validos.length;
    if (malos > 0) {
      toast.error(
        malos === 1
          ? `«${files.find((file) => !esAceptado(file))?.name}» no vale: solo PDF, JPG, PNG o WEBP.`
          : `${malos} ficheros no valen: solo PDF, JPG, PNG o WEBP.`,
      );
    }
    if (validos.length === 0) return;
    setSubiendo(kind);
    subirVarios.mutate({ kind, files: validos });
  }

  function soltarEnFila(kind: DeedKind, event: DragEvent<HTMLLIElement>) {
    event.preventDefault();
    event.stopPropagation();
    setSobre(null);
    setArrastrando(false);
    if (!canManage || ocupado) return;

    const files = Array.from(event.dataTransfer.files);
    if (MULTIPLES.has(kind)) {
      empezarSubidaVarios(kind, files);
      return;
    }
    const file = files[0];
    if (!file) return;
    /*
     * Solo el primero. Cada fila es UN documento —la nota simple es una— y repartir los demás
     * por las filas de al lado sería adivinar dónde van; quien suelta cuatro ficheros en
     * «IBI» no está diciendo dónde van los otros tres.
     */
    if (files.length > 1) {
      toast.info(`Cada fila guarda un documento. He cogido «${file.name}».`);
    }
    empezarSubida(kind, file);
  }

  /** El ojo: se abre en el visor. */
  function ver(deed: PropertyDeedDto) {
    if (!deed.file) return;
    visor.open({ id: deed.file.documentId, name: deed.file.name, meta: deed.kindLabel });
  }

  /** La hoja y el botón de descarga: se baja el fichero directamente, sin pasar por el visor. */
  function descargar(deed: PropertyDeedDto) {
    if (!deed.file) return;
    void api
      .downloadDocument(deed.file.documentId, deed.file.name)
      .catch(() => toast.error('No se ha podido descargar el documento.'));
  }

  const puestos = DEED_KINDS.filter((kind) => porTipo.has(kind)).length;
  const conHuecos = EN_GRUPOS.size;

  /*
   * La carpeta tal y como la tiene un propietario: cinco grupos con sus documentos dentro. Los
   * tipos heredados se cuelan al final del último grupo SOLO si tienen fichero: esconder algo
   * que existe es peor que una lista un poco más larga.
   */
  const grupos = DEED_GROUPS.map((group, index) => ({
    ...group,
    kinds:
      index === DEED_GROUPS.length - 1
        ? [...group.kinds, ...HEREDADOS.filter((kind) => porTipo.has(kind))]
        : group.kinds,
  }));

  return (
    <Card variant="sm">
      <CardHeader
        title="Documentos del inmueble"
        subtitle={
          canManage
            ? `${puestos} de ${conHuecos} documentos. Arrastra cada papel a su fila, o púlsala para elegirlo.`
            : `${puestos} de ${conHuecos} documentos.`
        }
      />

      {/*
        El contenedor de la lista escucha el arrastre por dos motivos: encender las filas en
        cuanto el fichero entra —una zona de destino que solo aparece cuando ya estás encima
        llega tarde— y TRAGARSE el que se suelte entre dos filas. Sin lo segundo el navegador
        abre el PDF en la pestaña y se lleva por delante lo que hubiera a medias.
      */}
      <div
        className={arrastrando ? `${styles.zona} ${styles.zonaActiva}` : styles.zona}
        onDragEnter={(event) => {
          if (canManage && traeFicheros(event)) setArrastrando(true);
        }}
        onDragOver={(event) => {
          if (canManage && traeFicheros(event)) event.preventDefault();
        }}
        onDragLeave={(event) => {
          // Pasar de un hijo a otro dispara `dragleave` en el padre. Solo cuenta si el puntero
          // sale de verdad; si no, la lista parpadea entera al recorrerla.
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setArrastrando(false);
            setSobre(null);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          setArrastrando(false);
          setSobre(null);
        }}
      >
      {grupos.map((grupo) => (
        <section key={grupo.key} className={styles.group}>
          <h4 className={styles.groupTitle}>{grupo.label}</h4>
          <ul className={styles.list}>
            {grupo.kinds.map((kind) => {
              const multiple = MULTIPLES.has(kind);
              const varios = todosPorTipo.get(kind) ?? [];
              // En un tipo múltiple la fila es el cajón: sus documentos van debajo, uno por línea.
              const deed = multiple ? undefined : porTipo.get(kind);
              const hayAlgo = multiple ? varios.length > 0 : Boolean(deed);
              const cargando = subiendo === kind && ocupado;
              const encima = sobre === kind;
              const destino = canManage && !ocupado;

              return (
                <Fragment key={kind}>
                <li
                  className={[
                    styles.row,
                    hayAlgo ? styles.rowDone : '',
                    // Se marca como disponible en cuanto entra un fichero en la tarjeta, y con
                    // más fuerza la que tiene el puntero encima.
                    arrastrando && destino ? styles.rowDisponible : '',
                    encima && destino ? styles.rowEncima : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onDragEnter={(event) => {
                    if (destino && traeFicheros(event)) setSobre(kind);
                  }}
                  onDragOver={(event) => {
                    if (!destino || !traeFicheros(event)) return;
                    // Sin `preventDefault` en CADA `dragover` el navegador no admite el soltar.
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                    if (sobre !== kind) setSobre(kind);
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setSobre((actual) => (actual === kind ? null : actual));
                    }
                  }}
                  onDrop={(event) => soltarEnFila(kind, event)}
                >
                  {/* Con fichero, la hoja es un botón que lo descarga. */}
                  {deed?.file ? (
                    <button
                      type="button"
                      className={`${styles.icon} ${styles.iconBoton}`}
                      onClick={() => descargar(deed)}
                      aria-label={`Descargar ${DEED_KIND_LABELS[kind]}`}
                      title="Descargar"
                    >
                      <Icon name="file-lines" size={17} color="var(--naar-brand-ink)" />
                    </button>
                  ) : (
                    <span className={styles.icon}>
                      <Icon
                        name={hayAlgo ? 'file-lines' : 'file'}
                        size={17}
                        color={hayAlgo ? 'var(--naar-brand-ink)' : 'var(--naar-text-faint)'}
                      />
                    </span>
                  )}

                  <span className={styles.main}>
                    <span className={styles.name}>{DEED_KIND_LABELS[kind]}</span>
                    <span className={styles.meta}>
                      {multiple
                        ? lineaVarios(varios.length, { cargando, soltable: encima && destino })
                        : segundaLinea(deed, { cargando, soltable: encima && destino })}
                    </span>
                  </span>

                  <span className={styles.actions}>
                    {deed?.file && (
                      <button
                        type="button"
                        className={styles.action}
                        onClick={() => ver(deed)}
                        aria-label={`Ver ${DEED_KIND_LABELS[kind]}`}
                        title="Ver el documento"
                      >
                        <Icon name="eye" size={16} strokeWidth={2} />
                      </button>
                    )}

                    {deed?.file && (
                      <button
                        type="button"
                        className={styles.action}
                        onClick={() => descargar(deed)}
                        aria-label={`Descargar ${DEED_KIND_LABELS[kind]}`}
                        title="Descargar"
                      >
                        <Icon name="download" size={16} strokeWidth={2} />
                      </button>
                    )}

                    {canManage && (
                      <button
                        type="button"
                        className={styles.action}
                        disabled={cargando}
                        onClick={() => {
                          destinoRef.current = kind;
                          if (inputRef.current) inputRef.current.multiple = multiple;
                          inputRef.current?.click();
                        }}
                        aria-label={
                          multiple
                            ? `Añadir documentos a ${DEED_KIND_LABELS[kind]}`
                            : deed
                              ? `Sustituir ${DEED_KIND_LABELS[kind]}`
                              : `Subir ${DEED_KIND_LABELS[kind]}`
                        }
                        title={multiple ? 'Añadir documentos' : deed ? 'Sustituir' : 'Subir'}
                      >
                        <Icon name={multiple ? 'plus' : 'upload'} size={16} strokeWidth={2} />
                      </button>
                    )}

                    {canManage && deed && (
                      <button
                        type="button"
                        className={`${styles.action} ${styles.actionDanger}`}
                        disabled={borrar.isPending}
                        onClick={() => borrar.mutate(deed.id)}
                        aria-label={`Quitar ${DEED_KIND_LABELS[kind]}`}
                        title="Quitar"
                      >
                        <Icon name="trash" size={15} strokeWidth={2} />
                      </button>
                    )}
                  </span>
                </li>

                {/* Los documentos de «Varios», cada uno con su ver y su quitar. */}
                {varios.map((item) => (
                  <li key={item.id} className={styles.subRow}>
                    {item.file ? (
                      <button
                        type="button"
                        className={`${styles.icon} ${styles.iconBoton} ${styles.iconSub}`}
                        onClick={() => descargar(item)}
                        aria-label={`Descargar ${item.file.name}`}
                        title="Descargar"
                      >
                        <Icon name="file-lines" size={15} color="var(--naar-brand-ink)" />
                      </button>
                    ) : (
                      <span className={`${styles.icon} ${styles.iconSub}`}>
                        <Icon name="file" size={15} color="var(--naar-text-faint)" />
                      </span>
                    )}
                    <span className={styles.main}>
                      <span className={styles.subName} title={item.file?.name ?? item.name}>
                        {item.file?.name ?? item.name}
                      </span>
                      <span className={styles.meta}>
                        {item.file ? formatShortDate(item.createdAt.slice(0, 10)) : 'ficha sin fichero'}
                      </span>
                    </span>
                    <span className={styles.actions}>
                      {item.file && (
                        <button
                          type="button"
                          className={styles.action}
                          onClick={() => ver(item)}
                          aria-label={`Ver ${item.file.name}`}
                          title="Ver el documento"
                        >
                          <Icon name="eye" size={16} strokeWidth={2} />
                        </button>
                      )}
                      {item.file && (
                        <button
                          type="button"
                          className={styles.action}
                          onClick={() => descargar(item)}
                          aria-label={`Descargar ${item.file.name}`}
                          title="Descargar"
                        >
                          <Icon name="download" size={16} strokeWidth={2} />
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className={`${styles.action} ${styles.actionDanger}`}
                          disabled={borrar.isPending}
                          onClick={() => borrar.mutate(item.id)}
                          aria-label={`Quitar ${item.file?.name ?? item.name}`}
                          title="Quitar"
                        >
                          <Icon name="trash" size={15} strokeWidth={2} />
                        </button>
                      )}
                    </span>
                  </li>
                ))}
                </Fragment>
              );
            })}
          </ul>
        </section>
      ))}
      </div>

      {/* Un solo input para toda la lista: `destinoRef` recuerda de qué fila salió el clic. */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className={styles.hiddenInput}
        onChange={(event) => {
          const file = event.target.files?.[0];
          const kind = destinoRef.current;
          if (kind && MULTIPLES.has(kind)) {
            empezarSubidaVarios(kind, Array.from(event.target.files ?? []));
            destinoRef.current = null;
            event.target.value = '';
            return;
          }
          // Por `empezarSubida`, igual que el soltar: una sola comprobación del tipo de fichero
          // para las dos vías, y el mismo mensaje cuando no vale.
          if (file && kind) empezarSubida(kind, file);
          destinoRef.current = null;
          // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
          event.target.value = '';
        }}
      />
    </Card>
  );
}

/** La segunda línea de una fila múltiple: cuántos hay, o qué pasa al soltar. */
function lineaVarios(cuantos: number, estado: { cargando: boolean; soltable: boolean }): string {
  if (estado.cargando) return 'Subiendo…';
  if (estado.soltable) return 'Suelta para añadirlos';
  if (cuantos === 0) return 'Pendiente · admite varios documentos';
  return cuantos === 1 ? '1 documento' : `${cuantos} documentos`;
}

/**
 * La segunda línea de cada fila.
 *
 * Cuatro estados en una función y no cuatro ternarias encajadas dentro del JSX: encajadas no
 * se leen, y esta línea es la que dice si el papel está, si falta o qué va a pasar al soltar.
 */
function segundaLinea(
  deed: PropertyDeedDto | undefined,
  estado: { cargando: boolean; soltable: boolean },
): string {
  if (estado.cargando) return 'Subiendo…';
  if (estado.soltable) return deed ? 'Suelta para sustituirlo' : 'Suelta para subirlo';
  if (!deed) return 'Pendiente';

  return (
    [
      deed.estateNumber ? `Finca ${deed.estateNumber}` : null,
      deed.registry,
      deed.file ? formatShortDate(deed.createdAt.slice(0, 10)) : 'ficha sin fichero',
    ]
      .filter(Boolean)
      .join(' · ') || 'Guardada'
  );
}
